import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';

import { LmHttpAdapter } from '@rcs/leave-management';
import { generateGetUrlFromConfigKeyFn } from '@rcs/utils';
import { ErsGenericExtService } from '../../../services/ers-generic-ext/ers-generic-ext.service';
import { selectedUserTenant } from '../../../base/+store/selectors/ers-user.selectors';
import { environment } from '../../../../environments/environment';

/**
 * App-level implementation of LmHttpAdapter.
 *
 * Bridges the leave-management library to the ERS app's HTTP infrastructure
 * (ErsGenericExtService, ErsUserFacade, HttpClient + environment).
 *
 * Provided at root level via app.module.ts:
 * ```ts
 * { provide: LmHttpAdapter, useClass: AppLmHttpAdapter }
 * ```
 */
@Injectable()
export class AppLmHttpAdapter extends LmHttpAdapter {
  private _gService   = inject(ErsGenericExtService);
  private _store      = inject(Store);
  private _http       = inject(HttpClient);

  get selectedUserTenant$() {
    return this._store.pipe(select(selectedUserTenant));
  }

  get<T>(endpoint: string, params?: any[]): Observable<T> {
    return this._gService.initiateGetRequest<T>(endpoint as any, params);
  }

  post<TBody, TResult>(endpoint: string, command: string, body: TBody): Observable<TResult> {
    return this._gService.initiatePostRequest<TBody, TResult>(endpoint as any, command, body);
  }

  put<TBody, TResult>(endpoint: string, command: string, body: TBody): Observable<TResult> {
    return this._gService.initiatePutRequest<TBody, TResult>(endpoint as any, command, body);
  }

  delete<T>(endpoint: string, params: any[]): Observable<T> {
    return this._gService.initiateDeleteRequest<T>(endpoint as any, params);
  }

  getFiltered<T>(endpoint: string, queryParams: Record<string, string>): Observable<T> {
    const baseUrl = generateGetUrlFromConfigKeyFn(
      endpoint as any,
      [],
      null,
      environment.apiUrl,
      false,
    );
    let params = new HttpParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      params = params.set(key, value);
    });
    return this._http.get<T>(baseUrl, { params });
  }

  getBlob(endpoint: string, urlParams?: any[]): Observable<Blob> {
    const url = generateGetUrlFromConfigKeyFn(
      endpoint as any,
      urlParams ?? [],
      null,
      environment.apiUrl,
      false,
    );
    return this._http.get(url, { responseType: 'blob' });
  }

  getFileInfo(
    endpoint: string,
    urlParams?: any[],
  ): Observable<{ contentLength: number | null; contentType: string }> {
    const url = generateGetUrlFromConfigKeyFn(
      endpoint as any,
      urlParams ?? [],
      null,
      environment.apiUrl,
      false,
    );
    return this._http.get<{ contentLength: number | null; contentType: string }>(url);
  }

  uploadDocument(
    file: File,
    tenantId: string,
    folder = 'documents',
  ): Observable<{ fileUrl?: string; fileName?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${environment.apiUrl.documentUpload}Profile/AttachDocument?tenantId=${encodeURIComponent(tenantId)}&folder=${encodeURIComponent(folder)}`;
    return this._http
      .post<any>(url, formData)
      .pipe(
        map((response) => ({
          fileUrl:  response?.value?.documentPath  || response?.fileUrl,
          fileName: response?.value?.documentName  || response?.fileName || file.name,
        })),
      );
  }
}
